import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { isValidCnpj as isValidCnpjNumeric, maskCnpj as maskCnpjNumeric, maskCpfHidden, maskCpf as maskCpfNumeric, unmaskCnpj as unmaskCnpjNumeric, } from '@ibk/shared-utils';
function normalizeMaskedDocument(value) {
    return value?.replaceAll(/[.\-/]/g, '').toUpperCase() ?? '';
}
function charToAlphanumericValue(char) {
    if (/\d/.test(char))
        return Number.parseInt(char, 10);
    return (char.toUpperCase().codePointAt(0) ?? 0) - 48;
}
function checkValidationDigitAlphanumeric(identifier, validationDigit) {
    const sum = identifier
        .split('')
        .reverse()
        .reduce((acc, item, i) => acc + ((i % 8) + 2) * charToAlphanumericValue(item), 0);
    let result = sum % 11;
    if (result < 2) {
        result = 0;
    }
    else {
        result = 11 - result;
    }
    return result.toString() === validationDigit;
}
function maskCnpjAlphanumeric(value) {
    const cleaned = value
        .replaceAll(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 14);
    return cleaned
        .replace(/([a-zA-Z0-9]{2})([a-zA-Z0-9])/, '$1.$2')
        .replace(/([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2')
        .replace(/([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1/$2')
        .replace(/([a-zA-Z0-9]{4})([a-zA-Z0-9]{1,2})$/, '$1-$2')
        .slice(0, 18);
}
function unmaskCnpjAlphanumeric(value) {
    return value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
}
function maskCnpjHiddenAlphanumeric(value) {
    const valueToHide = value.includes('*') ? value : value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return `**.${valueToHide.substring(2, 5)}.${valueToHide.substring(5, 8)}/****-**`;
}
function maskDocumentHiddenAlphanumeric(document) {
    const cleaned = document.replaceAll(/[^a-zA-Z0-9*]/g, '');
    if (cleaned.length === 11)
        return maskCpfHidden(document);
    if (cleaned.length === 14)
        return maskCnpjHiddenAlphanumeric(document);
}
function isValidCnpjAlphanumeric(value) {
    if (!value)
        return false;
    if (value.length === 14 && value.includes('*'))
        return true;
    const identifier = value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (identifier.length === 14) {
        const body = identifier.slice(0, 12);
        const firstValidationDigit = identifier[12];
        const secondValidationDigit = identifier[13];
        return (checkValidationDigitAlphanumeric(body, firstValidationDigit) &&
            checkValidationDigitAlphanumeric(body + firstValidationDigit, secondValidationDigit));
    }
    return false;
}
function maskCnpjAlphanumericMasked(value) {
    const cleaned = normalizeMaskedDocument(value).slice(0, 14);
    return cleaned
        .replace(/([a-zA-Z0-9*]{2})([a-zA-Z0-9*])/, '$1.$2')
        .replace(/([a-zA-Z0-9*]{3})([a-zA-Z0-9*])/, '$1.$2')
        .replace(/([a-zA-Z0-9*]{3})([a-zA-Z0-9*])/, '$1/$2')
        .replace(/([a-zA-Z0-9*]{4})([a-zA-Z0-9*]{1,2})$/, '$1-$2')
        .slice(0, 18);
}
function isValidCnpjAlphanumericMasked(value) {
    if (!value)
        return false;
    const normalized = normalizeMaskedDocument(value);
    if (normalized.includes('*')) {
        return normalized.length === 14;
    }
    if (normalized.length !== 14)
        return false;
    const body = normalized.slice(0, 12);
    const firstValidationDigit = normalized[12];
    const secondValidationDigit = normalized[13];
    return (checkValidationDigitAlphanumeric(body, firstValidationDigit) &&
        checkValidationDigitAlphanumeric(body + firstValidationDigit, secondValidationDigit));
}
function maskCpfHiddenLocal(clean) {
    return `***.${clean.substring(3, 6)}.${clean.substring(6, 9)}-**`;
}
function maskCnpjHiddenLocal(clean) {
    return `**.${clean.substring(2, 5)}.${clean.substring(5, 8)}/****-**`;
}
function maskDocumentHiddenImpl(value) {
    const clean = normalizeMaskedDocument(value);
    if (clean.length === 11)
        return maskCpfHiddenLocal(clean);
    if (clean.length === 14)
        return maskCnpjHiddenLocal(clean);
}
export function useCnpjAlphanumeric() {
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isAlphanumericEnabled = !!isFeatureFlagWhiteListed(FeatureFlagUseCase.PLATFORM_ALPHANUMERIC_CNPJ_ENABLED);
    const activeMaskCnpjMasked = isAlphanumericEnabled ? maskCnpjAlphanumericMasked : maskCnpjNumeric;
    const activeIsValidCnpjMasked = isAlphanumericEnabled ? isValidCnpjAlphanumericMasked : isValidCnpjNumeric;
    const formatDocumentMasked = (value) => {
        const clean = normalizeMaskedDocument(value);
        if (clean.length > 11)
            return activeMaskCnpjMasked(value);
        return maskCpfNumeric(value);
    };
    if (isAlphanumericEnabled) {
        return {
            isAlphanumericEnabled: true,
            maskCnpj: maskCnpjAlphanumeric,
            unmaskCnpj: unmaskCnpjAlphanumeric,
            isValidCnpj: isValidCnpjAlphanumeric,
            maskCnpjMasked: maskCnpjAlphanumericMasked,
            isValidCnpjMasked: activeIsValidCnpjMasked,
            formatDocumentMasked,
            maskDocumentHidden: maskDocumentHiddenAlphanumeric,
        };
    }
    return {
        isAlphanumericEnabled: false,
        maskCnpj: maskCnpjNumeric,
        unmaskCnpj: unmaskCnpjNumeric,
        isValidCnpj: isValidCnpjNumeric,
        maskCnpjMasked: maskCnpjNumeric,
        isValidCnpjMasked: activeIsValidCnpjMasked,
        formatDocumentMasked,
        maskDocumentHidden: maskDocumentHiddenImpl,
    };
}
